import { default as React, useEffect, useState } from 'react';
import slugify from 'slugify';
import styled from 'styled-components';
import { Grid } from '@fastly/consistently';

import EventButton from '@components/EventButton';
import LiveIcon from '@components/LiveIcon';
import { EventHeading, Text } from '@components/Typography';
import { EventModes, useEventModeContext } from '@hooks/use-event-mode';
import { useUrlWithUtmParams } from '@hooks/use-utm-params';

type EventContainerProps = {
  eventMode: EventModes;
};

const EventContainer = styled.div<EventContainerProps>`
  position: relative;
  display: grid;
  gap: 18px;
  padding-bottom: 32px;
  border-bottom: 1px solid var(--color-black);

  grid-template-columns: ${({ eventMode }) => eventMode === EventModes.Before && '2fr 1fr'};

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    grid-template-columns: 2fr 1fr;
  }

  &:hover {
    h5 {
      color: var(--color-blue);
      cursor: pointer;
    }
  }
`;

const EventHeadingContainer = styled(Grid)`
  flex-grow: 1;
`;

const SpecialLink = styled(Text)`
  cursor: pointer;
  position: relative;
  z-index: 10;
  display: block;
  color: var(--color-link);
  text-decoration: underline;
  margin-top: 0.5rem;

  &:hover {
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid var(--color-focus);
  }
`;

const AiringInfoContainer = styled.div`
  display: flex;
  gap: 24px;
  flex-direction: row;
  justify-content: flex-end;
  flex-shrink: 0;
  flex-grow: 1;
  flex-basis: 160px;

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    flex-direction: column;
    text-align: right;
  }
`;

const AiringNow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  order: -1;

  > ${LiveIcon} {
    margin-right: 12px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    order: 0;
  }
`;

const AirDate = styled(Text)`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    span {
      display: block;
    }
  }
`;

type Props = {
  session: GatsbyTypes.SessionFragment;
  onGuestClick: (content: GatsbyTypes.SessionFragment, type: 'event', sessionMode: EventModes) => unknown;
};

export const getEventMode = (sessionDate: Date, isLive: boolean | null): EventModes => {
  if (isLive) return EventModes.Live;

  const now = new Date();

  if (now < sessionDate) return EventModes.Before;

  return EventModes.After;
};

type AiringInfoArgs = {
  mode: EventModes;
  displayDate: string;
  displayTime: string;
  session: GatsbyTypes.SessionFragment;
};

const AiringInfo = ({ mode, displayDate, displayTime, session }: AiringInfoArgs): JSX.Element => {
  const url = `https://fastly.brandlive.com/dept-of-know-speaker-series/en/session/${session.brandLiveUrl}`;
  const urlWithUtmParams = useUrlWithUtmParams(url);
  switch (mode) {
    case EventModes.Before:
      return (
        <AiringInfoContainer>
          <AirDate variant="muted">
            <span>Watch live on</span> <span>{displayDate}</span> <span>{displayTime}</span>
          </AirDate>
        </AiringInfoContainer>
      );
    case EventModes.Live:
      return (
        <AiringInfoContainer>
          <EventButton
            hoverTriggerClass={EventContainer}
            href={urlWithUtmParams}
            dataLink={slugify(`join live ${session.id}`)}
            target="_blank"
          >
            Join Live
          </EventButton>
          <AiringNow>
            <LiveIcon />
            <Text variant="muted">Airing Now</Text>
          </AiringNow>
        </AiringInfoContainer>
      );
    case EventModes.After:
      return (
        <AiringInfoContainer>
          <Grid gap="8px" cols={2} xl={1} width={'100%'}>
            <EventButton
              order={1}
              href={urlWithUtmParams}
              dataLink={slugify(`watch recording ${session.id}`)}
              target="_blank"
              hoverTriggerClass={EventContainer}
            >
              Watch now
            </EventButton>
            <Text variant="muted" size="xs">
              {session.duration} minutes <br /> Aired {displayDate}
            </Text>
          </Grid>
        </AiringInfoContainer>
      );
  }
};

const Event = ({ session, onGuestClick }: Props): JSX.Element | null => {
  const [{ liveSession }] = useEventModeContext();
  const isLive: boolean = liveSession === session.id;

  const sessionDate: Date = session.date ? new Date(session.date) : new Date();
  const displayDate: string = sessionDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  // state + effect to only calculate and display time client-side
  // since SSG time will not match the user
  const [displayTime, setDisplayTime] = useState('');
  useEffect(() => {
    setDisplayTime(sessionDate.toLocaleTimeString('en-US', { timeStyle: 'short' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const eventMode = getEventMode(sessionDate, isLive);

  return (
    <EventContainer eventMode={eventMode}>
      <EventHeadingContainer gap="16px">
        <button onClick={() => onGuestClick(session, 'event', eventMode)}>
          <EventHeading live={isLive}>{session.title}</EventHeading>
        </button>
        <div>
          {session.guest ? (
            <Text as="p" variant="muted" style={{ display: 'inline-block', marginRight: '1ch' }}>
              With {session.guest.name}
            </Text>
          ) : null}
          <SpecialLink
            as="button"
            size="sm"
            onClick={() => onGuestClick(session, 'event', eventMode)}
            data-link-id={slugify(`learn more ${session.title?.toLowerCase()}`)}
          >
            Learn more
          </SpecialLink>
        </div>
      </EventHeadingContainer>
      <AiringInfo mode={eventMode} displayDate={displayDate} displayTime={displayTime} session={session} />
    </EventContainer>
  );
};

export default Event;
